import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Heart,
  CalendarDays,
  Clock,
  ArrowRight,
  User,
} from "lucide-react";
import { API_URL } from "./Config/Env";
import Swal from "sweetalert2";
import WhatsAppButton from "./Components/WhatsAppButton";

export default function BlogDetail() {
  const { id } = useParams();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liking, setLiking] = useState(false);

  const fetchBlog = async () => {
    setLoading(true);

    try {
      const res = await axios.get(
        `${API_URL}/get_blog_post/${id}/`
      );

      setBlog(res.data.post);
    } catch (error) {
      console.error("Error fetching blog:", error);

      Swal.fire({
        icon: "error",
        title: "Unable to Load Article",
        text: "We couldn't load this article. Please try again later.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const handleLike = async (blog_id) => {
    const user_id = localStorage.getItem("user_id");

    if (!user_id) {
      Swal.fire({
        icon: "warning",
        title: "Sign In Required",
        text: "Please sign in to like this article.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    setLiking(true);

    try {
      const response = await axios.get(
        `${API_URL}/like_blog_post/${user_id}/${blog_id}/`
      );

      if (response.status === 200) {
        await fetchBlog();
      }
    } catch (error) {
      console.error("Error liking blog:", error);

      Swal.fire({
        icon: "error",
        title: "Action Failed",
        text: "We couldn't update your like. Please try again.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setLiking(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-KE", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const calculateReadingTime = (content = "") => {
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / 200);

    return `${minutes || 1} min read`;
  };

  /* Loading State */
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        {/* Hero skeleton */}
        <section className="bg-slate-950 pt-28 pb-20 px-6">
          <div className="max-w-4xl mx-auto animate-pulse">
            <div className="h-4 w-32 bg-slate-700 rounded mb-8" />

            <div className="h-12 bg-slate-700 rounded-xl mb-4" />
            <div className="h-12 w-4/5 bg-slate-700 rounded-xl mb-8" />

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-xl bg-slate-700" />

              <div>
                <div className="h-4 w-32 bg-slate-700 rounded mb-2" />
                <div className="h-3 w-24 bg-slate-700 rounded" />
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-6 py-16 animate-pulse">
          <div className="h-[420px] bg-slate-200 rounded-3xl mb-12" />

          <div className="max-w-3xl mx-auto space-y-4">
            <div className="h-4 bg-slate-200 rounded" />
            <div className="h-4 bg-slate-200 rounded" />
            <div className="h-4 bg-slate-200 rounded" />
            <div className="h-4 w-3/4 bg-slate-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  /* Not Found */
  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-lg text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
            <span className="text-2xl font-bold">!</span>
          </div>

          <h1 className="text-3xl font-bold text-slate-900 mb-3">
            Article not found
          </h1>

          <p className="text-slate-600 mb-8">
            The article you're looking for may have been removed or is no
            longer available.
          </p>

          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition"
          >
            <ArrowLeft size={17} />
            Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-hidden">
      {/* Article Hero */}
      <section className="relative bg-slate-950 text-white pt-24 pb-32 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto">
          {/* Back link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-12 text-sm font-medium"
          >
            <ArrowLeft size={17} />
            Back to Insights
          </Link>

          {/* Category */}
          <div className="mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold">
              Nexindi Insights
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-[1.1] tracking-tight max-w-5xl mb-8">
            {blog.title}
          </h1>

          {/* Author + Metadata */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
            {/* Author */}
            <div className="flex items-center gap-3">
              {blog.profile_image ? (
                <img
                  src={blog.profile_image}
                  alt={blog.name || "Author"}
                  className="w-12 h-12 rounded-xl object-cover border border-white/20"
                />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center">
                  <User size={20} />
                </div>
              )}

              <div>
                <p className="font-semibold text-white">
                  {blog.name || "Nexindi Tech"}
                </p>

                <p className="text-sm text-slate-400">
                  Nexindi Contributor
                </p>
              </div>
            </div>

            {/* Separator */}
            <div className="hidden sm:block h-10 w-px bg-white/10" />

            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <CalendarDays size={16} className="text-blue-400" />
              {formatDate(blog.created_at)}
            </div>

            {/* Reading time */}
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Clock size={16} className="text-blue-400" />
              {calculateReadingTime(blog.content)}
            </div>
          </div>
        </div>
      </section>

      {/* Main Article */}
      <main className="relative px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {/* Featured image */}
          {blog.image && (
            <div className="relative -mt-20 z-20 rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/20 mb-16">
              <img
                src={blog.image}
                alt={blog.title}
                className="w-full h-[280px] md:h-[480px] lg:h-[560px] object-cover"
              />

              <div className="absolute inset-0 ring-1 ring-inset ring-black/10 rounded-3xl" />
            </div>
          )}

          {/* Article layout */}
          <div className="grid lg:grid-cols-[1fr_220px] gap-14 max-w-5xl mx-auto">
            {/* Article Content */}
            <article>
              <div
                className="
                  text-slate-700
                  text-[17px]
                  md:text-lg
                  leading-[1.9]
                  whitespace-pre-line
                "
              >
                {blog.content}
              </div>

              {/* Like Section */}
              <div className="mt-14 pt-8 border-t border-slate-200">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">
                      Enjoyed this article?
                    </h3>

                    <p className="text-sm text-slate-500">
                      Show your support by liking it.
                    </p>
                  </div>

                  <button
                    onClick={() => handleLike(blog.id)}
                    disabled={liking}
                    className="group inline-flex items-center justify-center gap-3 px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 transition-all duration-300 disabled:opacity-60"
                  >
                    <Heart
                      size={18}
                      className="transition-transform group-hover:scale-110"
                    />

                    {liking ? "Updating..." : `${blog.likes_count || 0} Likes`}
                  </button>
                </div>
              </div>

              {/* Author Card */}
              <div className="mt-10 rounded-3xl bg-slate-50 border border-slate-200 p-7 md:p-8">
                <div className="flex flex-col sm:flex-row gap-5">
                  {blog.profile_image ? (
                    <img
                      src={blog.profile_image}
                      alt={blog.name || "Author"}
                      className="w-16 h-16 rounded-2xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0">
                      <User size={25} />
                    </div>
                  )}

                  <div>
                    <p className="text-xs uppercase tracking-wider font-semibold text-blue-600 mb-2">
                      Written by
                    </p>

                    <h3 className="text-xl font-bold text-slate-900">
                      {blog.name || "Nexindi Tech"}
                    </h3>

                    <p className="text-slate-600 leading-relaxed mt-2">
                      Sharing practical perspectives on software, digital
                      products, innovation, and the technology shaping modern
                      businesses.
                    </p>
                  </div>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-28">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-5">
                  Article
                </p>

                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-slate-400 mb-1">Published</p>
                    <p className="font-semibold text-slate-800">
                      {formatDate(blog.created_at)}
                    </p>
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <p className="text-slate-400 mb-1">Reading time</p>
                    <p className="font-semibold text-slate-800">
                      {calculateReadingTime(blog.content)}
                    </p>
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <p className="text-slate-400 mb-1">Published by</p>
                    <p className="font-semibold text-slate-800">
                      Nexindi Tech
                    </p>
                  </div>

                  <div className="border-t border-slate-200 pt-5">
                    <Link
                      to="/blog"
                      className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700"
                    >
                      More Insights
                      <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="relative max-w-7xl mx-auto overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-600 via-blue-700 to-slate-950 px-7 py-16 md:px-16 md:py-20 text-white">
          <div className="absolute -top-32 right-0 w-[400px] h-[400px] bg-cyan-300/15 rounded-full blur-[120px]" />

          <div className="relative z-10 grid lg:grid-cols-[1fr_auto] items-center gap-10">
            <div className="max-w-3xl">
              <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-4">
                From Insight to Execution
              </p>

              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-5">
                Have a technology challenge to solve?
              </h2>

              <p className="text-blue-100 text-lg leading-relaxed">
                Nexindi Tech helps businesses and organizations turn ideas into
                websites, mobile applications, business systems, and
                intelligent digital products.
              </p>
            </div>

            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-3 bg-white text-blue-700 px-7 py-4 rounded-xl font-bold hover:bg-blue-50 transition-all duration-300 hover:-translate-y-1"
            >
              Start a Project

              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
}